import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { Booking, BookingService } from 'us-web-services';
import { Button, ButtonType } from '../Button';
import { Modal } from '../Modal';

// Sitter-side decline flow. Writes a PATCH with status=sitter_reject.
const MESSAGE_MAX = 500;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  booking: Booking;
  onDeclined?: () => void;
}

export const DeclineBookingModal = ({
  isOpen,
  onClose,
  booking,
  onDeclined,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const submit = async (): Promise<void> => {
    if (!booking.id) return;
    if (!message.trim()) {
      setError(t('components.modals.declineBooking.messageRequired'));
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await BookingService.patch(
        booking.id,
        {
          id: booking.id,
          message,
          status: { code: 'sitter_reject' },
        } as unknown as Booking,
      );
      toast.success(t('components.modals.declineBooking.success'));
      onDeclined?.();
      onClose();
    } catch {
      setError(t('components.modals.declineBooking.submitError'));
    } finally {
      setSubmitting(false);
    }
  };

  const remaining = MESSAGE_MAX - message.length;
  const parentName = booking.parent?.firstName ?? booking.parent?.name ?? '';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('components.modals.declineBooking.title')}
      className='w-full max-w-lg'
      footer={
        <>
          <Button buttonType={ButtonType.tertiary} onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button buttonType={ButtonType.primary} disabled={submitting} onClick={submit}>
            {t('common.decline')}
          </Button>
        </>
      }
    >
      <p className='mb-3'>
        {t('components.modals.declineBooking.intro', {
          name: parentName || t('components.modals.declineBooking.introFallbackName'),
        })}
      </p>
      <div className='text-right italic text-xs text-neutral-500'>
        {t('components.modals.declineBooking.charactersRemaining', { count: remaining })}
      </div>
      <textarea
        aria-label={t('components.modals.declineBooking.messageAriaLabel')}
        className='form-control w-full border border-neutral-300 rounded p-2 mb-3'
        rows={5}
        maxLength={MESSAGE_MAX}
        value={message}
        onChange={(e): void => setMessage(e.target.value)}
      />
      {error && <p className='text-red-600 text-sm mb-2'>{error}</p>}
    </Modal>
  );
};
